import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Eye, Pencil, MessageCircle, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { RegistrationForm } from "@/components/RegistrationForm";
import { REG_STATUSES, REG_SOURCES, regStatusLabel, regStatusCls, waDigits } from "@/lib/registrationMeta";

const inputCls = "px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";

export default function Pendaftaran() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", program: "", source: "", date_from: "", date_to: "" });
  const pageSize = 10;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/registrations", { params: { ...filters, page, page_size: pageSize } });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error("Gagal memuat pendaftaran."); } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  const setF = (k) => (e) => { setPage(1); setFilters((p) => ({ ...p, [k]: e.target.value })); };
  const onSaved = () => { setShowForm(false); setEditing(null); load(); };
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="text-2xl font-bold text-slate-900">Pendaftaran</h1><p className="text-sm text-slate-500">{total} pendaftaran calon siswa di cabang Anda</p></div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="registration-add-button" className="inline-flex items-center gap-1.5 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg"><Plus className="h-4 w-4" /> Pendaftaran Baru</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={`${inputCls} w-full pl-9`} placeholder="Cari nama, no. pendaftaran, WA…" value={filters.q} onChange={setF("q")} data-testid="registration-search-input" />
        </div>
        <select className={inputCls} value={filters.status} onChange={setF("status")} data-testid="filter-reg-status"><option value="">Semua Status</option>{REG_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}</select>
        <input className={inputCls} placeholder="Program…" value={filters.program} onChange={setF("program")} data-testid="filter-reg-program" />
        <select className={inputCls} value={filters.source} onChange={setF("source")} data-testid="filter-reg-source"><option value="">Semua Sumber</option>{REG_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <input type="date" className={inputCls} value={filters.date_from} onChange={setF("date_from")} data-testid="filter-reg-date-from" title="Dari tanggal" />
        <input type="date" className={inputCls} value={filters.date_to} onChange={setF("date_to")} data-testid="filter-reg-date-to" title="Sampai tanggal" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="registration-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-3">No. Pendaftaran</th><th className="text-left px-4 py-3">Nama</th><th className="text-left px-4 py-3">WhatsApp</th><th className="text-left px-4 py-3">Program</th><th className="text-left px-4 py-3">Sumber</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Tanggal</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="registration-empty">Belum ada pendaftaran. Klik "Pendaftaran Baru".</td></tr>
              ) : items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`registration-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.reg_no}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{r.full_name}</td>
                  <td className="px-4 py-3 text-slate-600">{r.phone_normalized || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.program_interest || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{r.source || "-"}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs border ${regStatusCls(r.status)}`}>{regStatusLabel(r.status)}</span></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.registration_date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => navigate(`/pendaftaran/${r.id}`)} data-testid={`registration-view-${r.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Detail / Ubah Status"><Eye className="h-4 w-4" /></button>
                      <button onClick={() => { setEditing(r); setShowForm(true); }} data-testid={`registration-edit-${r.id}`} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Edit"><Pencil className="h-4 w-4" /></button>
                      {r.phone_normalized && <a href={`https://wa.me/${waDigits(r.phone_normalized)}`} target="_blank" rel="noreferrer" data-testid={`registration-wa-${r.id}`} className="p-1.5 rounded hover:bg-green-100 text-green-600" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-sm text-slate-500">
          <span>Halaman {page} dari {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="reg-page-prev" className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="reg-page-next" className="p-1.5 rounded border border-slate-200 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {showForm && <RegistrationForm mode={editing ? "edit" : "add"} initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />}
    </AppLayout>
  );
}
